import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, UsersIcon, CheckCircle2Icon, HeartPulseIcon, TrendingUpIcon, TargetIcon, HandshakeIcon, CheckSquareIcon, PresentationIcon, BarChart3Icon, MenuIcon, XIcon, GraduationCapIcon, SparklesIcon, BookOpenIcon, ZapIcon, CompassIcon, RotateCcwIcon, ShieldCheckIcon, MessageSquareIcon, QuoteIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
export function ForHRPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const nav = [{
    label: 'For HR Teams',
    href: '#hero'
  }, {
    label: 'How It Works',
    href: '#solution'
  }, {
    label: 'Learning Pathways',
    href: '#learning'
  }, {
    label: 'Reports',
    href: '#reporting'
  }, {
    label: 'Pricing',
    href: '#pricing'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-3648">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3649">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-3650">
          <Link to="/" data-id="element-3651">
            <Logo data-id="element-3652" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-3653">
            <ul className="flex items-center gap-8" data-id="element-3654">
              {nav.map(item => <li key={item.label} data-id="element-3655">
                  <a href={item.href} className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-3656">
                    {item.label}
                  </a>
                </li>)}
            </ul>
            <Button variant="primary" href="/designer" data-id="element-3657">
              Design Free Offsite
            </Button>
          </nav>
          <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-id="element-3658">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-3659" /> : <MenuIcon className="w-6 h-6" data-id="element-3660" />}
          </button>
        </div>
        {mobileOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg py-4 px-6 flex flex-col gap-4" data-id="element-3661">
            {nav.map(item => <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-slate-800 py-2 border-b border-slate-50" data-id="element-3662">
                {item.label}
              </a>)}
            <Button variant="primary" className="w-full mt-2" href="/designer" data-id="element-3663">
              Design Free Offsite
            </Button>
          </div>}
      </header>

      {/* Hero */}
      <section id="hero" className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden bg-white" data-id="element-3664">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
        backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
      }} data-id="element-3665" />

        {/* Floating ambient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-3666">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.07] rounded-full blur-3xl" data-id="element-3667" />
          <motion.div animate={{
          x: [0, -25, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-3668" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3669">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3670">
            <div className="lg:col-span-6" data-id="element-3671">
              <FadeIn data-id="element-3672">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm relative overflow-hidden" data-id="element-3673">
                  <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider relative z-10" data-id="element-3674">
                    For HR Teams
                  </span>
                  <span className="text-sm font-medium text-slate-600 relative z-10" data-id="element-3675">
                    People-led offsite design
                  </span>
                  <motion.span aria-hidden initial={{
                  x: '-120%'
                }} animate={{
                  x: '320%'
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: 'easeInOut'
                }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent skew-x-12 pointer-events-none" data-id="element-3676" />
                </div>
              </FadeIn>

              <FadeIn delay={0.05} data-id="element-3677">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6 text-brand-navy" data-id="element-3678">
                  Make your next offsite{' '}
                  <span className="relative inline-block whitespace-nowrap" data-id="element-3679">
                    <span className="relative z-10" data-id="element-3680">more than</span>
                    <motion.span initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: 1
                  }} transition={{
                    duration: 0.9,
                    delay: 0.6,
                    ease: [0.65, 0, 0.35, 1]
                  }} style={{
                    transformOrigin: 'left'
                  }} className="absolute bottom-1 left-0 right-0 h-3 bg-brand-coral/25 -z-0" data-id="element-3681" />
                  </span>{' '}
                  a company trip.
                </h1>
              </FadeIn>

              <FadeIn delay={0.15} data-id="element-3682">
                <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl" data-id="element-3683">
                  Offsite+ helps HR and leadership teams design, run, and
                  measure structured offsites with attendee intake, learning
                  pathways, wellness preferences, group matching, reflection
                  prompts, and post-event accountability.
                </p>
              </FadeIn>

              <FadeIn delay={0.25} data-id="element-3684">
                <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-3685">
                  <Button variant="primary" size="lg" href="/designer" className="group" data-id="element-3686">
                    Design Your Free Offsite
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3687" />
                  </Button>
                  <Button variant="outline" size="lg" data-id="element-3688">
                    Book HR Demo
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.35} data-id="element-3689">
                <div className="border-t border-slate-100 pt-8" data-id="element-3690">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-3691">
                    Used by people teams designing offsites that stick
                  </p>
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-slate-400" data-id="element-3692">
                    {['LATTICE', 'CULTURE AMP', 'GUSTO', 'BAMBOOHR', 'WORKDAY'].map(b => <span key={b} className="font-bold text-sm tracking-[0.2em]" data-id="element-3693">
                        {b}
                      </span>)}
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* HR-specific dashboard */}
            <FadeIn delay={0.4} className="lg:col-span-6 relative lg:h-[640px] flex items-center justify-center" data-id="element-3694">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10 rounded-full blur-3xl" data-id="element-3695" />

              {/* Decorative orbital ring */}
              <motion.div aria-hidden animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="hidden lg:block absolute w-[520px] h-[520px] rounded-full border border-dashed border-slate-200/80" data-id="element-3696">
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_0_4px_rgba(14,165,164,0.15)]" data-id="element-3697" />
                <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-brand-coral" data-id="element-3698" />
              </motion.div>

              {/* Floating accent — top left */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: -10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.1,
              duration: 0.6
            }} className="hidden md:block absolute top-8 -left-2 lg:left-4 z-20" data-id="element-3699">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white rounded-xl shadow-xl shadow-slate-300/40 border border-slate-100 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3700">
                  <div className="bg-emerald-50 p-1.5 rounded-md" data-id="element-3701">
                    <TrendingUpIcon className="w-4 h-4 text-emerald-600" data-id="element-3702" />
                  </div>
                  <div data-id="element-3703">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3704">
                      Connection Lift
                    </div>
                    <div className="text-sm font-bold text-brand-navy" data-id="element-3705">
                      +1.7 pts
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating accent — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.3,
              duration: 0.6
            }} className="hidden md:block absolute bottom-10 -right-2 lg:right-4 z-20" data-id="element-3706">
                <motion.div animate={{
                y: [0, 8, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-brand-navy text-white rounded-xl shadow-xl shadow-slate-900/20 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3707">
                  <div className="bg-brand-coral/20 p-1.5 rounded-md" data-id="element-3708">
                    <CheckSquareIcon className="w-4 h-4 text-brand-coral" data-id="element-3709" />
                  </div>
                  <div data-id="element-3710">
                    <div className="text-[10px] font-semibold text-brand-coral uppercase tracking-wider" data-id="element-3711">
                      Commitments
                    </div>
                    <div className="text-sm font-bold" data-id="element-3712">43 captured</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              y: 40,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.8
            }} whileHover={{
              y: -4
            }} className="relative w-full max-w-lg mx-auto bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden z-10 transition-shadow hover:shadow-slate-400/40" data-id="element-3713">
                <div className="bg-brand-navy text-white p-5 relative overflow-hidden" data-id="element-3714">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-3715" />
                  <div className="relative z-10" data-id="element-3716">
                    <div className="flex items-center gap-2 mb-1.5" data-id="element-3717">
                      <span className="bg-brand-teal/15 text-teal-300 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-3718">
                        Team Offsite
                      </span>
                    </div>
                    <h3 className="text-xl font-bold" data-id="element-3719">Leadership Reset</h3>
                    <div className="flex items-center gap-3 text-xs text-slate-300 mt-1" data-id="element-3720">
                      <span className="flex items-center gap-1" data-id="element-3721">
                        <UsersIcon className="w-3 h-3" data-id="element-3722" /> 64 attendees
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4 bg-slate-50/50" data-id="element-3723">
                  {/* Goals */}
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3724">
                    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700" data-id="element-3725">
                      <TargetIcon className="w-4 h-4 text-brand-teal" data-id="element-3726" />
                      Goals
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-3727">
                      {['Alignment', 'Trust', 'Wellness'].map(g => <span key={g} className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-md font-medium" data-id="element-3728">
                          {g}
                        </span>)}
                    </div>
                  </div>

                  {/* Before/After */}
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3729">
                    <div className="flex justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-3730">
                      <span data-id="element-3731">Before / After Scores</span>
                    </div>
                    <div className="space-y-3" data-id="element-3732">
                      {[{
                      label: 'Connection',
                      before: 6.4,
                      after: 8.1
                    }, {
                      label: 'Clarity',
                      before: 6.2,
                      after: 8.4
                    }, {
                      label: 'Energy',
                      before: 5.8,
                      after: 7.6
                    }].map(s => <div key={s.label} data-id="element-3733">
                          <div className="flex justify-between text-xs mb-1" data-id="element-3734">
                            <span className="text-slate-600 font-medium" data-id="element-3735">
                              {s.label}
                            </span>
                            <span className="font-mono font-semibold" data-id="element-3736">
                              <span className="text-slate-400" data-id="element-3737">{s.before}</span>
                              <span className="text-brand-teal mx-1" data-id="element-3738">→</span>
                              <span className="text-brand-navy" data-id="element-3739">{s.after}</span>
                            </span>
                          </div>
                          <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-3740">
                            <div className="absolute top-0 left-0 h-full bg-slate-300" style={{
                          width: `${s.before / 10 * 100}%`
                        }} data-id="element-3741" />
                            <motion.div className="absolute top-0 h-full bg-brand-teal" initial={{
                          width: 0,
                          left: `${s.before / 10 * 100}%`
                        }} animate={{
                          width: `${(s.after - s.before) / 10 * 100}%`
                        }} transition={{
                          duration: 1.2,
                          delay: 0.5
                        }} data-id="element-3742" />
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Status row */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-3743">
                    <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm flex items-center gap-2" data-id="element-3744">
                      <div className="bg-brand-coral/10 p-2 rounded-md" data-id="element-3745">
                        <CheckSquareIcon className="w-4 h-4 text-brand-coral" data-id="element-3746" />
                      </div>
                      <div data-id="element-3747">
                        <div className="text-xs text-slate-500 font-medium" data-id="element-3748">
                          Commitments
                        </div>
                        <div className="text-sm font-bold text-brand-navy" data-id="element-3749">
                          43 captured
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm flex items-center gap-2" data-id="element-3750">
                      <div className="bg-brand-navy/10 p-2 rounded-md" data-id="element-3751">
                        <BarChart3Icon className="w-4 h-4 text-brand-navy" data-id="element-3752" />
                      </div>
                      <div data-id="element-3753">
                        <div className="text-xs text-slate-500 font-medium" data-id="element-3754">
                          Report
                        </div>
                        <div className="text-sm font-bold text-brand-teal" data-id="element-3755">
                          Ready
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Outcomes Leadership Cares About */}
      <Section className="bg-brand-navy text-white relative overflow-hidden py-16" data-id="element-3756">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3757">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-teal/20 rounded-full blur-3xl" data-id="element-3758" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-coral/20 rounded-full blur-3xl" data-id="element-3759" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-3760">
          <FadeIn className="text-center mb-12" data-id="element-3761">
            <h2 className="text-2xl font-bold text-slate-300 uppercase tracking-widest" data-id="element-3762">
              Outcomes leadership cares about
            </h2>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-3763">
            {[{
            value: '+27%',
            label: 'Engagement Lift'
          }, {
            value: '8.7/10',
            label: 'Avg. Satisfaction'
          }, {
            value: '91%',
            label: 'Would Recommend'
          }, {
            value: '73%',
            label: 'Commitment Follow-through'
          }].map((stat, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-3764">
                <div className="text-4xl md:text-5xl font-black text-white mb-2" data-id="element-3765">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-brand-teal uppercase tracking-wider" data-id="element-3766">
                  {stat.label}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Problem */}
      <Section className="bg-slate-50" data-id="element-3767">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3768">
          <FadeIn data-id="element-3769">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3770">
              Offsites are expensive. The outcomes are often unclear.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3771">
              People teams plan flights, rooms, and dinners. But the experience
              that drives alignment, connection, and follow-through usually gets
              left to chance.
            </p>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3772">
          {['People arrive with different expectations', 'The agenda feels generic', 'Quiet voices get missed', 'Team issues stay under the surface', 'Wellness needs are overlooked', 'Follow-through fades after everyone returns'].map((p, i) => <FadeIn key={p} delay={i * 0.05} data-id="element-3773">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 h-full flex items-start gap-3 shadow-sm" data-id="element-3774">
                <div className="w-2 h-2 rounded-full bg-brand-coral mt-2 shrink-0" data-id="element-3775" />
                <p className="text-slate-700 font-medium" data-id="element-3776">{p}</p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Before / After Transformation */}
      <Section className="bg-white border-y border-slate-100" data-id="element-3777">
        <div className="max-w-5xl mx-auto" data-id="element-3778">
          <FadeIn className="text-center mb-12" data-id="element-3779">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3780">
              From generic trips to measurable programs.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3781">
              The difference between a company vacation and a strategic offsite.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-3782">
            {/* Before */}
            <FadeIn delay={0.1} data-id="element-3783">
              <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 h-full" data-id="element-3784">
                <div className="flex items-center gap-3 mb-6" data-id="element-3785">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center" data-id="element-3786">
                    <XIcon className="w-5 h-5 text-slate-500" data-id="element-3787" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-700" data-id="element-3788">
                    Generic Offsite
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-3789">
                  {['Agenda built in a spreadsheet', 'Dietary needs collected via email', 'Forced icebreakers that feel awkward', 'No clear goals or baseline metrics', 'Vague "good vibes" after the event'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-600" data-id="element-3790">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-3791" />
                      <span data-id="element-3792">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            {/* After */}
            <FadeIn delay={0.2} data-id="element-3793">
              <div className="bg-brand-navy rounded-3xl p-8 border border-brand-navy shadow-xl h-full relative overflow-hidden" data-id="element-3794">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl" data-id="element-3795" />
                <div className="relative z-10" data-id="element-3796">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3797">
                    <div className="w-10 h-10 rounded-full bg-brand-teal flex items-center justify-center" data-id="element-3798">
                      <CheckCircle2Icon className="w-5 h-5 text-white" data-id="element-3799" />
                    </div>
                    <h3 className="text-xl font-bold text-white" data-id="element-3800">
                      Measurable Program
                    </h3>
                  </div>
                  <ul className="space-y-4" data-id="element-3801">
                    {['AI-designed learning pathways', 'Branded intake for goals & wellness', 'Structured networking and prompts', 'Before/after connection scoring', 'Executive-ready reporting'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-3802">
                        <CheckCircle2Icon className="w-5 h-5 text-brand-teal shrink-0" data-id="element-3803" />
                        <span data-id="element-3804">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Solution */}
      <Section id="solution" className="bg-slate-50" data-id="element-3805">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3806">
          <FadeIn data-id="element-3807">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3808">
              A structured experience before, during, and after the offsite.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-3809">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-200 z-0" data-id="element-3810"></div>
          {[{
          title: 'Before',
          color: 'text-brand-teal',
          bg: 'bg-brand-teal/10',
          items: ['Collect goals', 'Understand team needs', 'Set expectations', 'Prepare attendees']
        }, {
          title: 'During',
          color: 'text-brand-coral',
          bg: 'bg-brand-coral/10',
          items: ['Guide reflection', 'Support group conversations', 'Encourage connection', 'Capture commitments']
        }, {
          title: 'After',
          color: 'text-brand-navy',
          bg: 'bg-brand-navy/10',
          items: ['Measure improvement', 'Track commitments', 'Summarize themes', 'Support follow-through']
        }].map((c, i) => <FadeIn key={c.title} delay={i * 0.15} className="relative z-10" data-id="element-3811">
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-slate-100 h-full" data-id="element-3812">
                <div className={`${c.bg} ${c.color} w-12 h-12 rounded-xl mb-5 flex items-center justify-center font-bold`} data-id="element-3813">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-5" data-id="element-3814">
                  {c.title}
                </h3>
                <ul className="space-y-3" data-id="element-3815">
                  {c.items.map(it => <li key={it} className="flex items-center gap-2 text-slate-700 font-medium text-sm" data-id="element-3816">
                      <CheckCircle2Icon className={`w-4 h-4 ${c.color} shrink-0`} data-id="element-3817" />
                      {it}
                    </li>)}
                </ul>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Learning Pathways */}
      <Section id="learning" className="bg-white overflow-hidden" data-id="element-3818">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3819">
          <FadeIn data-id="element-3820">
            <div className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-6" data-id="element-3821">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-3822" /> Intelligence Engine
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-3823">
              AI-powered learning pathways built from proven ideas.
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-3824">
              The Offsite+ Intelligence Engine blends AI, bestselling leadership
              frameworks, and executive-education style thinking to create
              curated learning pathways for every group.
            </p>
            <Button variant="outline" size="lg" href="/designer" data-id="element-3825">
              <BookOpenIcon className="w-4 h-4 mr-2" data-id="element-3826" />
              Build a Pathway
            </Button>
          </FadeIn>

          <FadeIn delay={0.2} className="relative" data-id="element-3827">
            <div className="absolute inset-0 bg-brand-coral/5 rounded-full blur-3xl" data-id="element-3828" />
            <div className="relative bg-white rounded-2xl shadow-soft border border-slate-100 p-8" data-id="element-3829">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100" data-id="element-3830">
                <div className="flex items-center gap-2" data-id="element-3831">
                  <GraduationCapIcon className="w-5 h-5 text-brand-navy" data-id="element-3832" />
                  <h4 className="font-bold text-brand-navy" data-id="element-3833">
                    Pathway: Leadership Reset
                  </h4>
                </div>
                <span className="text-xs font-bold text-brand-teal bg-brand-teal/10 px-2.5 py-1 rounded-full" data-id="element-3834">
                  4 Modules
                </span>
              </div>

              <div className="relative space-y-6 before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-3835">
                {[{
                icon: CompassIcon,
                name: 'Strategic Clarity',
                duration: '90 min',
                color: 'text-brand-navy'
              }, {
                icon: HandshakeIcon,
                name: 'Team Trust & Vulnerability',
                duration: '120 min',
                color: 'text-brand-teal'
              }, {
                icon: MessageSquareIcon,
                name: 'Communication Under Pressure',
                duration: '90 min',
                color: 'text-brand-coral'
              }, {
                icon: RotateCcwIcon,
                name: 'Commitment & Accountability',
                duration: '60 min',
                color: 'text-emerald-600'
              }].map((m, i) => <div key={m.name} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-3836">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-100 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-3837">
                      <m.icon className={`w-4 h-4 ${m.color}`} data-id="element-3838" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3839">
                      <div className="flex items-center justify-between mb-1" data-id="element-3840">
                        <h5 className="font-bold text-brand-navy text-sm" data-id="element-3841">
                          {m.name}
                        </h5>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-3842">{m.duration}</div>
                    </div>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* World-Class Content Partners */}
      <Section className="bg-slate-50 border-y border-slate-100 py-16" data-id="element-3843">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3844">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs font-bold text-brand-navy uppercase tracking-wider mb-6 shadow-sm" data-id="element-3845">
            <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-3846" />
            World-Class Content
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-8" data-id="element-3847">
            Pathways built from frameworks your team respects.
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6 opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-3848">
            {['Harvard Business School', 'Wharton', 'INSEAD', 'Stanford GSB', 'MIT Sloan', 'Kellogg'].map(src => <div key={src} className="font-serif text-lg md:text-xl font-medium text-brand-navy" data-id="element-3849">
                {src}
              </div>)}
          </div>
        </div>
      </Section>

      {/* Reporting */}
      <Section id="reporting" className="bg-white" data-id="element-3850">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3851">
          <FadeIn data-id="element-3852">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-3853">
              Reports your CEO will actually read.
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-3854">
              A clean post-event report with engagement, before/after scores,
              themes, and commitments — ready to share with the executive team
              to prove the ROI of the offsite.
            </p>
            <Button variant="primary" size="lg" href="/report" data-id="element-3855">
              View Sample Report
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-3856" />
            </Button>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-3857">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-100 p-6 md:p-8" data-id="element-3858">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-3859">
                <h3 className="font-bold text-brand-navy" data-id="element-3860">Executive Summary</h3>
                <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1 rounded-full" data-id="element-3861">
                  Final Report
                </span>
              </div>
              <div className="space-y-4" data-id="element-3862">
                {[{
                label: 'Connection score',
                before: '6.4',
                after: '8.1',
                color: 'text-brand-teal'
              }, {
                label: 'Clarity score',
                before: '6.2',
                after: '8.4',
                color: 'text-brand-coral'
              }].map(m => <div key={m.label} className="bg-slate-50 p-4 rounded-xl flex items-center justify-between" data-id="element-3863">
                    <div className="text-sm font-semibold text-slate-700" data-id="element-3864">
                      {m.label}
                    </div>
                    <div className="font-mono text-sm font-bold" data-id="element-3865">
                      <span className="text-slate-400" data-id="element-3866">{m.before}</span>
                      <span className={`mx-2 ${m.color}`} data-id="element-3867">→</span>
                      <span className="text-brand-navy" data-id="element-3868">{m.after}</span>
                    </div>
                  </div>)}
                <div className="grid grid-cols-3 gap-3 pt-2" data-id="element-3869">
                  <div className="border border-slate-100 rounded-xl p-3 text-center" data-id="element-3870">
                    <div className="text-xl font-bold text-brand-navy" data-id="element-3871">43</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5" data-id="element-3872">
                      Commitments
                    </div>
                  </div>
                  <div className="border border-slate-100 rounded-xl p-3 text-center" data-id="element-3873">
                    <div className="text-xl font-bold text-brand-teal" data-id="element-3874">89%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5" data-id="element-3875">
                      Survey
                    </div>
                  </div>
                  <div className="border border-slate-100 rounded-xl p-3 text-center" data-id="element-3876">
                    <div className="text-xl font-bold text-brand-coral" data-id="element-3877">
                      91%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5" data-id="element-3878">
                      Recommend
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="bg-slate-50" data-id="element-3879">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3880">
          <FadeIn data-id="element-3881">
            <QuoteIcon className="w-12 h-12 text-brand-teal/20 mx-auto mb-6" data-id="element-3882" />
            <h3 className="text-2xl md:text-3xl font-medium text-brand-navy leading-relaxed mb-8" data-id="element-3883">
              "We used to spend weeks planning the logistics and zero time on
              the actual content. Offsite+ gave us a structured way to measure
              connection and prove to our CEO that the retreat was worth the
              investment."
            </h3>
            <div className="flex items-center justify-center gap-4" data-id="element-3884">
              <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden" data-id="element-3885">
                <img src="https://i.pravatar.cc/150?u=hr" alt="Sarah Jenkins" className="w-full h-full object-cover" data-id="element-3886" />
              </div>
              <div className="text-left" data-id="element-3887">
                <div className="font-bold text-brand-navy" data-id="element-3888">Sarah Jenkins</div>
                <div className="text-sm text-slate-500" data-id="element-3889">
                  Chief People Officer, TechFlow
                </div>
              </div>
              <div className="h-8 w-px bg-slate-200 mx-4" data-id="element-3890" />
              <div className="text-left" data-id="element-3891">
                <div className="text-lg font-bold text-brand-teal" data-id="element-3892">9.2/10</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-3893">
                  Avg Event Score
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="bg-white" data-id="element-3894">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3895">
          <FadeIn data-id="element-3896">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3897">
              Simple pricing for every offsite.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-7xl mx-auto" data-id="element-3898">
          {[{
          name: 'Free',
          price: '$0',
          desc: 'AI Offsite Designer',
          cta: 'Try Free',
          variant: 'outline' as const,
          highlight: false
        }, {
          name: 'Starter',
          price: '$499',
          period: '/event',
          desc: 'Small offsites & training days',
          cta: 'Start an Event',
          variant: 'primary' as const,
          highlight: false
        }, {
          name: 'Professional',
          price: '$1,500',
          period: '/event',
          desc: 'Best for HR-led retreats',
          cta: 'Launch',
          variant: 'accent' as const,
          highlight: true
        }, {
          name: 'Premium',
          price: '$3,500',
          period: '/event',
          desc: 'Leadership & destination retreats',
          cta: 'Build',
          variant: 'primary' as const,
          highlight: false
        }, {
          name: 'Enterprise',
          price: 'Custom',
          desc: 'Multi-event, multi-team',
          cta: 'Contact Sales',
          variant: 'primary' as const,
          highlight: false
        }].map(plan => <FadeIn key={plan.name} data-id="element-3899">
              <div className={`relative h-full flex flex-col bg-white rounded-2xl p-6 ${plan.highlight ? 'border-2 border-brand-coral shadow-lg z-10' : 'border border-slate-200 shadow-sm'}`} data-id="element-3900">
                {plan.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-coral text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap" data-id="element-3901">
                    Recommended
                  </div>}
                <h3 className="text-base font-bold text-brand-navy mb-2" data-id="element-3902">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-2" data-id="element-3903">
                  <span className="text-2xl font-black text-slate-800" data-id="element-3904">
                    {plan.price}
                  </span>
                  {plan.period && <span className="text-xs text-slate-500 font-medium" data-id="element-3905">
                      {plan.period}
                    </span>}
                </div>
                <p className="text-xs text-slate-600 mb-6 flex-1" data-id="element-3906">
                  {plan.desc}
                </p>
                <Button variant={plan.variant} size="sm" className="w-full" href={plan.name === 'Free' ? '/designer' : undefined} data-id="element-3907">
                  {plan.cta}
                </Button>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-brand-navy text-white relative overflow-hidden" data-id="element-3908">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3909">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-brand-teal/20 rounded-full blur-[120px]" data-id="element-3910" />
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-brand-coral/20 rounded-full blur-[120px]" data-id="element-3911" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-3912">
          <FadeIn data-id="element-3913">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance leading-tight text-white" data-id="element-3914">
              Design an offsite your team actually remembers — and your
              leadership can measure.
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10" data-id="element-3915">
              <Button variant="accent" size="lg" href="/designer" data-id="element-3916">
                Design Free Offsite
              </Button>
              <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10 hover:border-white/40" data-id="element-3917">
                Book HR Demo
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-10" data-id="element-3918">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-3919">
          <Logo data-id="element-3920" />
          <div className="flex gap-6 text-sm font-medium text-slate-500" data-id="element-3921">
            <Link to="/" className="hover:text-brand-navy transition-colors" data-id="element-3922">
              Home
            </Link>
            <Link to="/designer" className="hover:text-brand-navy transition-colors" data-id="element-3923">
              AI Designer
            </Link>
            <Link to="/report" className="hover:text-brand-navy transition-colors" data-id="element-3924">
              Sample Report
            </Link>
          </div>
          <div className="text-sm text-slate-400" data-id="element-3925">
            © {new Date().getFullYear()} Offsite+
          </div>
        </div>
      </footer>
    </div>;
}